import dayjs from 'dayjs';
import React, {FC, useCallback, useEffect, useState} from 'react';
import {Image, TouchableOpacity, View} from 'react-native';
import {validateDevisRapatriement} from '../../../../../common/utils/validationAssuranceRapatriement';
import {DevisRapatriementParam} from '../../../../../data/types/interface/Assurance/AssuranceRapatriement/devis';
import PrimaryButton from '../../../../components/Button/PrimaryButton';
import {buttonsStyles} from '../../../../components/Button/boutonStyle';
import {BoxShadowed} from '../../../../components/Container/BoxShadowed';
import {InputDatePicker} from '../../../../components/Inputs/InputDatePicker';
import {CountrySelect} from '../../../../components/Inputs/Select/CountrySelect';
import Label from '../../../../components/Label';
import ConfirmationModal from '../../../../components/Modal/ConfirmationModal';
import {styles} from './formStyle';

// eslint-disable-next-line @typescript-eslint/no-require-imports
const addIcon = require('../../../../../ressources/images/add-icon.png');
// eslint-disable-next-line @typescript-eslint/no-require-imports
const removeIcon = require('../../../../../ressources/images/remove.png');

interface Props {
  defaultData?: DevisRapatriementParam[];
  onCancel: () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onSubmit: (data: any) => void;
}

const initData = [
  {
    dateNaissance: '',
    paysResidence: '',
  },
];

const FormDevis: FC<Props> = ({defaultData, onCancel, onSubmit}) => {
  const [values, setValues] = React.useState<DevisRapatriementParam[]>(
    defaultData && defaultData.length > 0 ? defaultData : initData,
  );
  const [modalMessage, setModalMessage] = useState<string | null>(null);
  const [showErrors, setShowErrors] = React.useState<
    Record<keyof DevisRapatriementParam, boolean>[]
  >(() =>
    (defaultData && defaultData.length > 0 ? defaultData : initData).map(
      () => ({
        dateNaissance: false,
        paysResidence: false,
      }),
    ),
  );

  const addBeneficiaire = React.useCallback(() => {
    setValues(prev => [
      ...prev,
      {
        dateNaissance: '',
        paysResidence: '',
      },
    ]);

    setShowErrors(prev => [
      ...prev,
      {
        dateNaissance: false,
        paysResidence: false,
      },
    ]);
  }, [values]);

  const removeBeneficiaire = React.useCallback(
    (index: number) => {
      setValues(prev => [...prev.slice(0, index), ...prev.slice(index + 1)]);
      setShowErrors(prev => [
        ...prev.slice(0, index),
        ...prev.slice(index + 1),
      ]);
    },
    [values],
  );

  const updateBeneficiaire = React.useCallback(
    (index: number, value: DevisRapatriementParam) => {
      console.log(value);

      const copy = [...values];
      copy[index] = value;
      setValues(() => copy);

      const copyShowError = [...showErrors];
      copyShowError[index] = {
        paysResidence: value.paysResidence != '',
        dateNaissance: value.dateNaissance != '',
      };
      setShowErrors(() => copyShowError);
    },
    [values, showErrors],
  );

  const errors = React.useMemo<DevisRapatriementParam[]>(
    () => validateDevisRapatriement(values),
    [values],
  );

  const noError = useCallback(() => {
    return errors.every(e => e.dateNaissance == '' && e.paysResidence == '');
  }, [errors]);

  const handleSubmit = () => {
    setShowErrors(prev =>
      prev.map(() => ({paysResidence: true, dateNaissance: true})),
    );
    if (values.length > 0) {
      if (noError()) {
        onSubmit(values);
      }
    } else {
      setModalMessage(`Veuillez renseigner au moins un(e) assuré(e)`);
    }
  };

  const clearModalMessage = useCallback(() => {
    setModalMessage(() => null);
  }, []);

  useEffect(() => {
    console.log(values);
  }, [values]);

  return (
    <View style={styles.form}>
      <Label type="mainTitle" title="Bénéficiaires" />

      <BoxShadowed style={{marginBottom: 40, maxWidth: '95%'}}>
        {values.map((v, index) => (
          <View style={styles.inputRow} key={`benef_${index}`}>
            <View style={styles.inputGroup}>
              <View style={styles.inputGroupElement}>
                <InputDatePicker
                  name="dateNaissance"
                  required
                  label="Date de naissance"
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  onChange={(_name: string, value: any) => {
                    updateBeneficiaire(index, {
                      ...v,
                      dateNaissance: dayjs(value).format('YYYY-MM-DD'),
                    });
                  }}
                  value={v.dateNaissance}
                  dateMax={new Date()}
                  error={errors[index].dateNaissance}
                  showError={showErrors[index].dateNaissance}
                  minWidth={'100%'}
                  inputStyle={{marginTop: 10}}
                  noInit
                />
              </View>
              <View style={styles.inputGroupElement}>
                <CountrySelect
                  label="Pays de résidence"
                  name="paysResidence"
                  required
                  value={v.paysResidence ?? ''}
                  error={errors[index].paysResidence}
                  showError={showErrors[index].paysResidence}
                  onChange={value => {
                    updateBeneficiaire(index, {
                      ...v,
                      paysResidence: value,
                    });
                  }}
                  exceptCode={['MG']}
                />
              </View>
            </View>
            <View>
              <TouchableOpacity
                onPress={() => removeBeneficiaire(index)}
                style={styles.removeBtn}>
                <Image source={removeIcon} style={{width: 25, height: 25}} />
              </TouchableOpacity>
            </View>
          </View>
        ))}

        <TouchableOpacity style={[styles.addBtn]} onPress={addBeneficiaire}>
          <Image source={addIcon} style={{width: 20, height: 20}} />
          <Label
            textStyle={styles.addBtnText}
            title={'Ajouter un bénéficiaire'}
          />
        </TouchableOpacity>
      </BoxShadowed>

      <View
        style={[buttonsStyles.displayHorizontal, {zIndex: -1, marginTop: 20}]}>
        <PrimaryButton
          onPress={() => onCancel()}
          styleBtnTxt={buttonsStyles.btnAnnuler}
          styleBtnOuter={buttonsStyles.btnAnnulerOuterSimu}
          title="Annuler"
          type="annuler"
        />
        <PrimaryButton
          onPress={handleSubmit}
          styleBtnTxt={buttonsStyles.btnValider}
          styleBtnOuter={buttonsStyles.btnValiderOuterSimu}
          title="Générer devis"
          type="valider"
        />
      </View>
      {modalMessage && (
        <ConfirmationModal
          title={`Information`}
          content={modalMessage}
          btn1={'OK'}
          btn2={undefined}
          handleBtn1={clearModalMessage}
          handleBtn2={undefined}
        />
      )}
    </View>
  );
};

export default FormDevis;
